import { supabase } from '../lib/supabase';
import { accessControlService } from './accessControl';
import { QueryPerformanceMonitor } from '../utils/queryOptimizer';

export const matchService = {
  // Test function to check table access
  testTableAccess: async () => {
    try {
      // Test 0: Check Supabase configuration
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseKey) {
        return { success: false, error: 'Missing Supabase configuration' };
      }
      
      // Test 1: Check Supabase connection
      const { data: { user } } = await supabase.auth.getUser();
      
      // Test 2: Simple select without joins
      await supabase
        .from('matches')
        .select('id, name')
        .limit(1);

      // Test 3: Check if user is authenticated
      // Test 4: Check if we can access users table
      if (user?.email) {
        await supabase
          .from('users')
          .select('id, email')
          .eq('email', user.email)
          .single();
      }

      // Test 5: Check if matches table exists
      await supabase
        .from('matches')
        .select('count')
        .limit(0);

      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  },

  // Get match details by ID with enhanced team and official information (access-controlled)
  getMatchDetails: async (matchId: string) => {
    try {
      const result = await accessControlService.getMatchDetails(matchId);
      
      if (result.error) {
        return { data: null, error: result.error };
      }

      return { data: result.data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get match officials with official details
  getMatchOfficials: async (matchId: string) => {
    try {
      const { data, error } = await supabase
        .from('match_officials')
        .select(`
          *,
          official:official_id(
            id,
            name,
            certification_level,
            date_of_birth,
            avatar,
            country,
            experience_years,
            specializations,
            contact_info
          )
        `)
        .eq('match_id', matchId);

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get all matches with team information (access-controlled and optimized)
  getAllMatches: async () => {
    const queryId = `getAllMatches_${Date.now()}`;
    QueryPerformanceMonitor.startQuery(queryId);
    
    try {
      console.log('🔍 Fetching matches with access control and optimization...');
      
      const result = await accessControlService.getMatchesWithTeams();
      
      if (result.error) {
        console.error('❌ Error fetching matches:', result.error);
        return { data: null, error: result.error };
      }

      const duration = QueryPerformanceMonitor.endQuery(queryId);
      console.log(`✅ Matches fetched successfully: ${result.data?.length || 0} matches in ${duration}ms`);
      return { data: result.data, error: null };
    } catch (error) {
      QueryPerformanceMonitor.endQuery(queryId);
      console.error('❌ Exception in getAllMatches:', error);
      return { data: null, error };
    }
  },

  // Get match lineup (placeholder for future implementation)
  getMatchLineup: async (matchId: string) => {
    try {
      // This will be implemented when we add the lineup tables
      return { data: [], error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get match events (placeholder for future implementation)
  getMatchEvents: async (matchId: string) => {
    try {
      // This will be implemented when we add the events table
      return { data: [], error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get penalty shootout scores for multiple matches
  getPenaltyShootoutScores: async (matchIds: string[]) => {
    try {
      // First, let's check what stat_keys exist for penalty shootout
      const { error: statError } = await supabase
        .from('stat_types')
        .select('id, stat_key')
        .in('stat_key', ['penalty_shootout_scored', 'penalty_shootout_missed']);
      
      if (statError) {
        console.error('Error fetching stat types:', statError);
      }

      const { data, error } = await supabase
        .from('scoresheet')
        .select(`
          match_id,
          team_id,
          value,
          stat_types!inner(stat_key)
        `)
        .in('match_id', matchIds)
        .in('stat_types.stat_key', ['penalty_shootout_scored', 'penalty_shootout_missed']);

      if (error) {
        console.error('Error fetching penalty shootout scores:', error);
        return { data: null, error };
      }

      // Process the data to group by match and team
      const penaltyScores: Record<string, { home: { scored: number; missed: number }, away: { scored: number; missed: number } }> = {};
      
      // Initialize all matches
      matchIds.forEach(matchId => {
        penaltyScores[matchId] = {
          home: { scored: 0, missed: 0 },
          away: { scored: 0, missed: 0 }
        };
      });

      // Get match details to determine home/away teams
      const { data: matches, error: matchesError } = await supabase
        .from('matches')
        .select('id, home_team_id, away_team_id')
        .in('id', matchIds);

      if (matchesError) {
        console.error('Error fetching match details:', matchesError);
        return { data: null, error: matchesError };
      }

      const matchTeamMap: Record<string, { homeTeamId: string; awayTeamId: string }> = {};
      matches?.forEach(match => {
        matchTeamMap[match.id] = {
          homeTeamId: match.home_team_id,
          awayTeamId: match.away_team_id
        };
      });

      // Process each score entry
      data?.forEach(entry => {
        const matchId = entry.match_id;
        const teamId = entry.team_id;
        const statKey = (entry.stat_types as any)?.stat_key;
        const value = entry.value || 0;

        if (!penaltyScores[matchId] || !matchTeamMap[matchId]) return;

        const { homeTeamId, awayTeamId } = matchTeamMap[matchId];
        const isHomeTeam = teamId === homeTeamId;
        const isAwayTeam = teamId === awayTeamId;

        if (isHomeTeam) {
          if (statKey === 'penalty_shootout_scored') {
            penaltyScores[matchId].home.scored += value;
          } else if (statKey === 'penalty_shootout_missed') {
            penaltyScores[matchId].home.missed += value;
          }
        } else if (isAwayTeam) {
          if (statKey === 'penalty_shootout_scored') {
            penaltyScores[matchId].away.scored += value;
          } else if (statKey === 'penalty_shootout_missed') {
            penaltyScores[matchId].away.missed += value;
          }
        }
      });

      return { data: penaltyScores, error: null };
    } catch (error) {
      console.error('Exception fetching penalty shootout scores:', error);
      return { data: null, error };
    }
  }
};
